import * as React from 'react'
import { clsx } from 'clsx'
import type * as Polymorphic from '@radix-ui/react-polymorphic'

import textColorStyles from '../../styles/atoms/text-color.module.css'
import textAlignStyles from '../../styles/atoms/text-align.module.css'
import iconSlotStyles from '../../styles/atoms/icon-slot.module.css'

import styles from './Text.module.css'

type TextColor =
  | 'default'
  | 'subtle'
  | 'inverted'
  | 'brand'
  | 'warning'
  | 'success'
  | 'critical'
  | 'upgrade'
  | 'inherit'
  | 'disabled'

type TextProps = {
  /**
   * Render as a different element
   * @default span
   */
  as?:
    | 'p'
    | 'div'
    | 'label'
    | 'span'
    | 'small'
    | 'legend'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'time'

  /**
   * Controls visual size of the text
   * @default md
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Align text to the left, center or right
   */
  align?: 'left' | 'center' | 'right'

  /**
   * Controls visual weight of the text
   * @default regular
   */
  weight?: 'regular' | 'medium' | 'semibold' | 'bold'

  /**
   * Controls line height of the text
   * @default regular
   */
  lineHeight?: 'tight' | 'regular'

  /**
   * Controls visual color of the text
   * @default inherit
   */
  color?: TextColor
  /**
   * Controls the text overflow behavior, adding ellipsis when the text exceeds the width of the container.
   * When using beware of [common flex and ellipsis pitfalls](https://css-tricks.com/flexbox-truncated-text/)
   * @default false
   */
  truncate?: boolean
  /**
   * Icon rendered before the text. It is centered on the first line, so it
   * stays centered for single-line text and aligns to the top when text wraps.
   */
  icon?: React.ReactNode
  /**
   * Overrides how the icon aligns with the text.
   * `center` centers the icon against the whole text block.
   * @default first-line
   */
  iconAlign?: 'first-line' | 'center'
  /**
   * Places the icon before or after the text.
   * @default start
   */
  iconPosition?: 'start' | 'end'
}

const ICON_ROW_JUSTIFY = {
  left: iconSlotStyles.justifyLeft,
  center: iconSlotStyles.justifyCenter,
  right: iconSlotStyles.justifyRight,
} as const

const Text = React.forwardRef(
  (
    {
      children,
      className,
      as,
      size = 'md',
      color = 'inherit',
      align,
      weight = 'regular',
      lineHeight = 'regular',
      truncate = false,
      icon,
      iconAlign = 'first-line',
      iconPosition = 'start',
      ...props
    },
    forwardedRef,
  ) => {
    const Tag = as || 'span'

    if (icon) {
      const iconSlot = (
        <span
          className={
            iconAlign === 'center'
              ? iconSlotStyles.center
              : iconSlotStyles.firstLine
          }
        >
          {icon}
        </span>
      )
      return (
        <Tag
          className={clsx(
            styles.base,
            iconSlotStyles.row,
            styles.withIcon,
            styles[size],
            styles[weight],
            styles[`lh-${lineHeight}`],
            textColorStyles[color],
            align && ICON_ROW_JUSTIFY[align],
            className,
          )}
          ref={forwardedRef}
          {...props}
        >
          {iconPosition === 'start' && iconSlot}
          <span
            className={clsx(iconSlotStyles.label, truncate && styles.truncate)}
          >
            {children}
          </span>
          {iconPosition === 'end' && iconSlot}
        </Tag>
      )
    }

    return (
      <Tag
        className={clsx(
          styles.base,
          styles[size],
          styles[weight],
          styles[`lh-${lineHeight}`],
          textColorStyles[color],
          align && textAlignStyles[align],
          truncate && styles.truncate,
          className,
        )}
        ref={forwardedRef}
        {...props}
      >
        {children}
      </Tag>
    )
  },
) as Polymorphic.ForwardRefComponent<'span', TextProps>

Text.displayName = 'Text'

export { Text }
export type { TextProps, TextColor }
